import { Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MediaAPI } from '@shared-features/services';
import { IconType } from '@shared/enums';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-avatar',
  template: `
    <div class="avatar__icon" [ngClass]="iconColorClass" *ngIf="!src && !title">
      <app-icon [name]="icon" [type]="iconType === 'material' ? IconType.MaterialDesign : IconType.SVG" [size]="iconSize"> </app-icon>
    </div>
    <div class="avatar__title" *ngIf="title && !src">
      {{ title }}
    </div>
    <div class="avatar__img" *ngIf="src">
      <img [src]="src" alt="" />
    </div>
  `,
  styles: [
    `
      :host.avatar {
        width: 3rem;
        height: 3rem;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: black;
        color: white;
        overflow: hidden;
        margin-bottom: 0;
      }

      :host.avatar--round {
        border-radius: 50%;
      }

      .avatar__text,
      .avatar__title,
      .avatar__img,
      .avatar__icon {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .avatar__img {
        width: 100%;
        height: 100%;
      }

      .avatar__img img {
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class AvatarComponent implements OnChanges, OnDestroy {
  @HostBinding('class.avatar') avatarClass = true;
  @Input() bgColor = '#eee';
  @Input() fgColor = '#222222';
  @Input() src = '';
  @Input() name = '';
  @Input() size = 48;
  @Input() round = false;
  @Input() icon = 'person';
  @Input() iconType: 'svg' | 'material' = 'material';
  @Input() radius = 0;
  @Input() mediaId: number;
  @Input() iconSize: 'xxs'|'xs' |'sm'| 'md' | 'lg' = 'xs';
  @Input() ringColor = '';
  @Input() iconColorClass = '';

  private destroy$ = new Subject<void>();
  private mediaId$ = new Subject<number>();

  constructor(private mediaApiService: MediaAPI) {
    this.mediaId$
      .pipe(
        switchMap((id) => this.mediaApiService.getMedia(id)),
        takeUntil(this.destroy$)
      )
      .subscribe((media) => (this.src = media.url));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostBinding('class.avatar--round') get roundClass(): boolean {
    return this.round;
  }

  @HostBinding('attr.style') get style(): string {
    const size = `${(this.size || 16) / 16}rem`;
    const fontSize = `${(this.size || 16) / 36}rem`;
    const borderRadius = this.round ? '50%' : `${(this.radius || 16) / 16}rem`;
    const ring = this.ringColor ? `outline: 2px solid ${this.ringColor}; outline-offset: 2px;` : '';
    return `
      width: ${size};
      height: ${size};
      font-size: ${fontSize};
      background-color: ${this.bgColor || ''};
      color: ${this.fgColor || ''};
      border-radius: ${borderRadius};
      ${ring}
    `;
  }

  title = '';

  ngOnChanges({ name, mediaId }: SimpleChanges): void {
    if (mediaId?.currentValue) {
      this.mediaId$.next(mediaId.currentValue);
    }
    if (name && name.currentValue?.length) {
      this.title = this.getInitials(this.name);
    }
  }

  private getInitials(value: string): string {
    return (value ?? '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase();
  }

  protected readonly IconType = IconType;
}
